import React, { useState, useEffect } from 'react';
import { searchContacts } from '../services/webRTCService';
import { CallEnd } from '@mui/icons-material';
import styled from '@emotion/styled';
import { validatePhoneNumberWithLeadingZeroFallback } from '@/utils/phone-validation';

const AvanserWebRTC = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;

    .numberBox {
        width: 100%;
        font-family: 'Roboto', sans-serif;
        font-size: 36px;
        font-weight: 500;
        line-height: 38px;
        color: #1d2939;
        margin-bottom: 0.8rem;
        margin-top: 0.6rem;
        padding: 0 0.5rem;
        text-align: center;
        outline: none;
        background-color: transparent;
        &:active,
        &:focus {
            border: none;
            outline: none;
        }
        &.error {
            color: #e74c3c;
        }
    }

    .deleteNumber {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        width: 68px;
        height: 68px;
        cursor: pointer;
        svg {
            width: 28px;
            height: 28px;
        }
    }

    .dial-pad {
        padding: 0 1rem;
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        grid-gap: 14px 24px;
        margin-bottom: 10px;

        button {
            display: flex;
            justify-content: center;
            align-items: center;
            font-family: 'Inter', sans-serif;
            width: 68px;
            height: 68px;
            aspect-ratio: 1;
            font-size: 30px;
            font-weight: 500;
            border: none;
            border-radius: 50%;
            background-color: rgb(234 236 240/1);
            color: #333;
            margin: auto;
            cursor: pointer;

            &:hover {
                background-color: #dededeff;
            }
        }
    }

    .callButton {
        svg {
            color: white;
            width: 24px;
            height: 24px;
        }
    }

    .tooltip {
        position: relative;
        display: inline-block;
        width: 100%;
    }

    .tooltip .tooltip-text {
        visibility: hidden;
        width: 100%;
        background-color: rgba(231, 76, 60, 0.9);
        color: white;
        text-align: center;
        border-radius: 6px;
        padding: 8px 12px;
        position: absolute;
        z-index: 1;
        /* bottom: 125%; */
        bottom: 75%;
        left: 50%;
        transform: translateX(-50%);
        opacity: 0;
        transition: opacity 0.3s;
        font-size: 14px;
        font-weight: 500;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }

    .tooltip .tooltip-text::after {
        content: '';
        position: absolute;
        /* top: 100%;
        left: 50%; */
        top: -9px;
        left: 15%;
        transform: rotate(180deg);
        margin-left: -5px;
        border-width: 5px;
        border-style: solid;
        border-color: rgba(231, 76, 60, 0.9) transparent transparent transparent;
    }

    .tooltip.show .tooltip-text {
        visibility: visible;
        opacity: 1;
    }
`;

const KeyPad = ({
    handleCall,
    updateCallName,
    auth,
    selectedBnum,
    directCall,
    setDirectCall,
    configuredUserAgent,
    isLoadingBnums,
    userAgent,
    setCallInProgress,
    setBnumsVisible,
    callStatus: propCallStatus,
    setCallStatus: setPropCallStatus,
}) => {
    const [dialNumber, setDialNumber] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [showTooltip, setShowTooltip] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    // Use the callStatus prop from parent (WebRTC) as the source of truth
    const callStatus = propCallStatus;

    // Track when user agent is connecting
    useEffect(() => {
        if (!userAgent) {
            setIsConnecting(false);
            return;
        }

        if (userAgent.isConnected()) {
            setIsConnecting(false);
        } else {
            setIsConnecting(true);
            const checkInterval = setInterval(() => {
                if (userAgent.isConnected()) {
                    setIsConnecting(false);
                    clearInterval(checkInterval);
                }
            }, 500);
            return () => clearInterval(checkInterval);
        }
    }, [userAgent]);

    // Reset call status when returning to keypad view (cleanup effect)
    useEffect(() => {
        return () => {
            // When component unmounts or when returning to keypad after a call,
            // reset the call status to ensure the button shows the correct state
            if (setPropCallStatus) {
                setPropCallStatus('');
            }
        };
    }, [setPropCallStatus]);

    const handleName = (contact) => {
        if (
            contact.firstNameLowerCase == null &&
            contact.lastNameLowerCase == null
        )
            return 'No name';
        if (
            contact.firstNameLowerCase == null &&
            contact.lastNameLowerCase != null
        )
            return `${contact.lastNameLowerCase.charAt(0).toUpperCase() + contact.lastNameLowerCase.slice(1)}`;
        if (
            contact.firstNameLowerCase != null &&
            contact.lastNameLowerCase == null
        )
            return `${contact.firstNameLowerCase.charAt(0).toUpperCase() + contact.firstNameLowerCase.slice(1)}`;
        return `${contact.firstNameLowerCase.charAt(0).toUpperCase() + contact.firstNameLowerCase.slice(1)} ${contact.lastNameLowerCase.charAt(0).toUpperCase() + contact.lastNameLowerCase.slice(1)}`;
    };

    // Validate phone number using E.164 validation with fallback to leading 0
    const isValidNumber = (number) => {
        if (!number || number.trim() === '') {
            return true; // Allow empty input during typing
        }

        // Determine country from selected Bnum
        const country = selectedBnum?.country?.toLowerCase();

        if (!country) {
            return true; // Allow input if country is not yet determined
        }

        const result = validatePhoneNumberWithLeadingZeroFallback(
            number,
            country
        );
        return result.isValid;
    };

    // Get formatted number for calling (with leading 0 if needed)
    const getFormattedNumber = (number) => {
        if (!number || number.trim() === '') {
            return number;
        }

        const country = selectedBnum?.country?.toLowerCase();

        if (!country) {
            return number;
        }

        const result = validatePhoneNumberWithLeadingZeroFallback(
            number,
            country
        );

        if (result.formattedNumber) {
            return result.formattedNumber;
        }

        // If no leading zero was added, return the original number
        return number;
    };

    useEffect(() => {
        if (
            !isLoadingBnums &&
            directCall &&
            ((configuredUserAgent === 'done' && userAgent) ||
                configuredUserAgent === 'no-bnum')
        ) {
            if (selectedBnum) {
                setDialNumber(directCall);

                // Use the directCall value directly in a separate function call
                const initiateDirectCall = async () => {
                    if (isValidNumber(directCall)) {
                        try {
                            // Wait for user agent to be connected and registered
                            if (
                                userAgent &&
                                (!userAgent.isConnected() ||
                                    !userAgent.isRegistered())
                            ) {
                                console.log(
                                    'Waiting for user agent to be ready...'
                                );

                                // Create a promise that resolves when the user agent is ready
                                const waitForUserAgent = new Promise(
                                    (resolve) => {
                                        // Check every 500ms if the user agent is ready
                                        const checkInterval = setInterval(
                                            () => {
                                                if (
                                                    userAgent.isConnected() &&
                                                    userAgent.isRegistered()
                                                ) {
                                                    clearInterval(
                                                        checkInterval
                                                    );
                                                    resolve();
                                                    console.log(
                                                        'resolved! User agent is ready'
                                                    );
                                                }
                                            },
                                            500
                                        );

                                        // Set a timeout of 10 seconds
                                        setTimeout(() => {
                                            clearInterval(checkInterval);
                                            resolve(); // Resolve anyway after timeout
                                        }, 10000);
                                    }
                                );

                                // Wait for the user agent to be ready
                                await waitForUserAgent;
                            }

                            // Get the formatted number (with leading 0 if needed)
                            const formattedNumber =
                                getFormattedNumber(directCall);

                            // Start the call immediately with empty name using the formatted number
                            handleCall('', formattedNumber);

                            // Run contact search in parallel with E.164 formatted number
                            searchContacts(
                                auth,
                                'search',
                                formattedNumber,
                                100,
                                false,
                                selectedBnum?.country
                            )
                                .then((data) => {
                                    // If contact found, update the call name
                                    if (data.data.contacts.length === 1) {
                                        updateCallName(
                                            handleName(data.data.contacts[0])
                                        );
                                    }
                                })
                                .catch((error) => {
                                    console.error(
                                        'Error fetching contact information:',
                                        error
                                    );
                                    // Continue with empty name on error
                                });
                        } catch (error) {
                            console.error(
                                'Error in initiateDirectCall:',
                                error
                            );
                            // Fallback: start call with formatted number
                            const formattedNumber =
                                getFormattedNumber(directCall);
                            handleCall('', formattedNumber);
                        }
                    } else {
                        // Not a valid number
                        if (setPropCallStatus) {
                            setPropCallStatus('');
                        }
                        const countryName =
                            selectedBnum?.country?.toUpperCase() ||
                            'the selected country';
                        setErrorMessage(
                            `Invalid phone number for ${countryName}. Please use a valid ${countryName} number format.`
                        );
                        setShowTooltip(true);
                        // Hide tooltip after 5 seconds
                        const timer = setTimeout(() => {
                            setShowTooltip(false);
                        }, 5000);

                        return () => clearTimeout(timer);
                    }
                };

                // Handle Call
                initiateDirectCall();
                setDirectCall(''); // Reset the direct call flag after successful call
            } else {
                // No Bnum selected
                if (setPropCallStatus) {
                    setPropCallStatus('');
                }
                setErrorMessage(
                    <>
                        No number selected, please first select a tracking
                        number by clicking{' '}
                        <span
                            style={{ color: 'gold', cursor: 'pointer' }}
                            onClick={() => {
                                setBnumsVisible(true);
                            }}
                        >
                            here
                        </span>
                    </>
                );
                setShowTooltip(true);
                setDirectCall('');

                // Hide tooltip after 5 seconds
                const timer = setTimeout(() => {
                    setShowTooltip(false);
                }, 5000);

                return () => clearTimeout(timer);
            }
        } else if (
            !directCall &&
            !isLoadingBnums &&
            !selectedBnum &&
            configuredUserAgent === 'no-bnum'
        ) {
            // setErrorMessage("No number selected, please first select a tracking number!");
            setErrorMessage(
                <>
                    No number selected, please first select a tracking number by
                    clicking{' '}
                    <span
                        style={{ color: 'gold', cursor: 'pointer' }}
                        onClick={() => {
                            setBnumsVisible(true);
                        }}
                    >
                        here
                    </span>
                </>
            );
            setShowTooltip(true);
            // Hide tooltip after 5 seconds
            const timer = setTimeout(() => {
                setShowTooltip(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [isLoadingBnums, selectedBnum, configuredUserAgent, userAgent]);

    const handleCallAction = async () => {
        if (callStatus === '') {
            if (!dialNumber || dialNumber.trim() === '') {
                setErrorMessage('Please enter a target number before calling');
                setShowTooltip(true);
                const timer = setTimeout(() => {
                    setShowTooltip(false);
                }, 5000);
                return () => clearTimeout(timer);
            }

            if (!isValidNumber(dialNumber)) {
                if (!selectedBnum) {
                    setErrorMessage(
                        <>
                            No number selected, please first select a tracking
                            number by clicking{' '}
                            <span
                                style={{ color: 'gold', cursor: 'pointer' }}
                                onClick={() => {
                                    setBnumsVisible(true);
                                }}
                            >
                                here
                            </span>
                        </>
                    );
                } else {
                    const countryName =
                        selectedBnum?.country?.toUpperCase() ||
                        'the selected country';
                    setErrorMessage(
                        `Invalid phone number for ${countryName}. Please use a valid ${countryName} number format.`
                    );
                }
                setShowTooltip(true);
                const timer = setTimeout(() => {
                    setShowTooltip(false);
                }, 5000);
                return () => clearTimeout(timer);
            }

            // Set call status to indicate call is in progress
            if (setPropCallStatus) {
                setPropCallStatus('connecting');
            }

            // Get the formatted number (with leading 0 if needed)
            const formattedNumber = getFormattedNumber(dialNumber);

            // Start the call immediately with empty name using the formatted number
            handleCall('', formattedNumber);

            // Run contact search in parallel with E.164 formatted number
            searchContacts(
                auth,
                'search',
                formattedNumber,
                100,
                false,
                selectedBnum?.country
            )
                .then((data) => {
                    // If contact found, update the call name
                    if (data.data.contacts.length === 1) {
                        updateCallName(handleName(data.data.contacts[0]));
                    }
                })
                .catch((error) => {
                    console.error('Error fetching contact information:', error);
                    // Continue with empty name on error
                });
        } else {
            // handleHangUp();
        }
    };

    const handleNumberClick = (number) => {
        const newNumber = dialNumber + number;
        handleDialNumberChange(newNumber);
    };

    const handleDeleteLastNumber = () => {
        const newNumber = dialNumber.slice(0, -1);
        handleDialNumberChange(newNumber);
    };

    const handleDialNumberChange = (number) => {
        setDialNumber(number);
        // Clear call status when user starts typing
        if (number.length > 0 && callStatus !== '') {
            if (setPropCallStatus) {
                setPropCallStatus('');
            }
        }

        // Determine country from selected Bnum
        const country = selectedBnum?.country?.toLowerCase();

        if (number && country) {
            const result = validatePhoneNumberWithLeadingZeroFallback(
                number,
                country
            );

            if (!result.isValid) {
                setErrorMessage(
                    result.errorMessage ||
                        `Invalid phone number for ${country.toUpperCase()}. Please use a valid ${country.toUpperCase()} number format.`
                );
                setShowTooltip(true);

                // Hide tooltip after 5 seconds
                const timer = setTimeout(() => {
                    setShowTooltip(false);
                }, 5000);

                return () => clearTimeout(timer);
            } else {
                setErrorMessage('');
                setShowTooltip(false);
            }
        } else if (number && !country) {
            // Show error if no country is selected
            setErrorMessage(
                <>
                    No number selected, please first select a tracking number by
                    clicking{' '}
                    <span
                        style={{ color: 'gold', cursor: 'pointer' }}
                        onClick={() => {
                            setBnumsVisible(true);
                        }}
                    >
                        here
                    </span>
                </>
            );
            setShowTooltip(true);

            // Hide tooltip after 5 seconds
            const timer = setTimeout(() => {
                setShowTooltip(false);
            }, 5000);

            return () => clearTimeout(timer);
        } else {
            setErrorMessage('');
            setShowTooltip(false);
        }
    };

    const dialPadNumbers = [
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '*',
        '0',
        '#',
    ];

    return (
        <>
            <AvanserWebRTC>
                <div className={`tooltip ${showTooltip ? 'show' : ''}`}>
                    <input
                        type="text"
                        value={dialNumber}
                        onChange={(event) =>
                            handleDialNumberChange(event.target.value)
                        }
                        className={`numberBox ${errorMessage ? 'error' : ''}`}
                    />
                    {errorMessage && (
                        <div className="tooltip-text">{errorMessage}</div>
                    )}
                </div>
                <div className="dial-pad">
                    {dialPadNumbers.map((number) => (
                        <button
                            key={number}
                            onClick={() => handleNumberClick(number)}
                        >
                            {number}
                        </button>
                    ))}
                    <div></div>
                    <button
                        onClick={handleCallAction}
                        className="callButton"
                        // style={{ backgroundColor: callStatus === "" ? '#88caa3' : '#e74c3c' }}
                        style={{
                            backgroundColor:
                                callStatus === ''
                                    ? isValidNumber(dialNumber)
                                        ? isConnecting
                                            ? '#f59e0b'
                                            : '#88caa3'
                                        : '#cccccc'
                                    : '#e74c3c',
                            cursor:
                                callStatus === '' &&
                                !isValidNumber(dialNumber) &&
                                dialNumber
                                    ? 'not-allowed'
                                    : isConnecting
                                      ? 'wait'
                                      : 'pointer',
                            opacity: isConnecting ? 0.8 : 1,
                        }}
                        disabled={
                            callStatus === '' &&
                            !isValidNumber(dialNumber) &&
                            dialNumber
                        }
                    >
                        {isConnecting ? (
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px',
                                }}
                            >
                                <svg
                                    className="spinner"
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        animation: 'spin 1s linear infinite',
                                    }}
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        strokeOpacity="0.25"
                                    ></circle>
                                    <path
                                        d="M4 12a8 8 0 018-8"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        strokeLinecap="round"
                                    ></path>
                                </svg>
                                <style>{`
                                    @keyframes spin {
                                        from {
                                            transform: rotate(0deg);
                                        }
                                        to {
                                            transform: rotate(360deg);
                                        }
                                    }
                                `}</style>
                            </div>
                        ) : callStatus === '' ? (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth="2"
                                stroke="currentColor"
                                aria-hidden="true"
                                className="phone-icon text-white"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M8.38 8.853a14.603 14.603 0 002.847 4.01 14.603 14.603 0 004.01 2.847c.124.06.187.09.265.112.28.082.625.023.862-.147.067-.048.124-.105.239-.219.35-.35.524-.524.7-.639a2 2 0 012.18 0c.176.115.35.29.7.64l.195.194c.532.531.797.797.942 1.082a2 2 0 010 1.806c-.145.285-.41.551-.942 1.082l-.157.158c-.53.53-.795.794-1.155.997-.4.224-1.02.386-1.478.384-.413-.001-.695-.081-1.26-.241a19.038 19.038 0 01-8.283-4.874A19.039 19.039 0 013.17 7.761c-.16-.564-.24-.846-.241-1.26a3.377 3.377 0 01.384-1.477c.202-.36.467-.625.997-1.155l.157-.158c.532-.53.798-.797 1.083-.941a2 2 0 011.805 0c.286.144.551.41 1.083.942l.195.194c.35.35.524.525.638.7a2 2 0 010 2.18c-.114.177-.289.352-.638.701-.115.114-.172.172-.22.238-.17.238-.228.582-.147.862.023.08.053.142.113.266z"
                                ></path>
                            </svg>
                        ) : (
                            <CallEnd sx={{ color: 'white', fontSize: 24 }} />
                        )}
                    </button>
                    <div
                        onClick={handleDeleteLastNumber}
                        className="deleteNumber"
                        style={{
                            visibility:
                                dialNumber.length > 0 ? 'visible' : 'hidden',
                        }}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="2"
                            stroke="currentColor"
                            aria-hidden="true"
                            className="delete-icon text-gray-800"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M17 9l-6 6m0-6l6 6M2.72 12.96l4.32 5.76c.352.47.528.704.751.873.198.15.421.262.66.33C8.72 20 9.013 20 9.6 20h7.6c1.68 0 2.52 0 3.162-.327a3 3 0 001.311-1.311C22 17.72 22 16.88 22 15.2V8.8c0-1.68 0-2.52-.327-3.162a3 3 0 00-1.311-1.311C19.72 4 18.88 4 17.2 4H9.6c-.587 0-.88 0-1.15.077a2 2 0 00-.659.33c-.223.169-.399.404-.751.873l-4.32 5.76c-.258.344-.387.516-.437.705a1 1 0 000 .51c.05.189.179.36.437.705z"
                            ></path>
                        </svg>
                    </div>
                </div>
            </AvanserWebRTC>
        </>
    );
};

export default KeyPad;
